import {
  ButtonHierarchy,
  ButtonSize,
  MainButton,
  Typography,
  TypographySize,
  TypographyWeight,
} from "@beydesign/storybook"
import { Box, Stack, useMediaQuery, useTheme } from "@mui/material"
import React, { Fragment, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { AgentVideo } from "../../components/AgentView/AgentVideo"
import Background from "../../components/background"
import Feedback from "../../components/Feedback/Feedback"
import { Header } from "../../components/header"
import Overlay from "../../components/Modal/Overlay"
import { PostScreenLoading } from "../../components/PostScreenLoading"
import { SnackbarWithText } from "../../components/snackbar"
import { getViewWidth } from "../../constants/styles"
import { endCallDb, getCall } from "../../external/firestore"
import { AnalyticsEvents } from "../../types/Analytics"
import { Call, CallDisconnect, CallDisconnectType } from "../../types/Call"
import { StateAction } from "../../types/State"
import { SnackbarType } from "../../types/UI"
import { isInIframe } from "../../utils/func"
import {
  getAgent,
  getAgentUsage,
  getDefaultAgentId,
  getDefaultCreatorId,
} from "../../utils/general"
import { useAppContext, useAppDispatchContext } from "../Context"
import { useMixpanel } from "../hooks/useMixpanel"

export function PostScreen() {
  const { id } = useParams()
  const context = useAppContext()
  const dispatch = useAppDispatchContext()
  const isEmbedded = isInIframe()
  const email = context.user.email
  const agent = context.agent
  const customBrandingFlow = context.customBrandingFlow || false

  const [call, setCall] = useState<Call | null>(null)
  const [isDataReady, setIsDataReady] = useState(false)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"))

  const navigate = useNavigate()
  const callDisconnectType = useLocation()?.state as CallDisconnectType | null
  const callDisconnect = callDisconnectType
    ? CallDisconnect[callDisconnectType]
    : null

  const [summaryOpen, setSummaryOpen] = useState(false)
  const [headline, setHeadline] = useState("You left the meeting")

  const isDefaultAgent = useMemo(() => {
    return agent?.creatorId === getDefaultCreatorId
  }, [agent?.creatorId])
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)

  const { logAnalyticsEvent } = useMixpanel()

  const hasCustomButton = useMemo(() => {
    return (
      agent?.custom_branding &&
      agent?.custom_brand_button_text &&
      agent?.custom_brand_button_url
    )
  }, [agent])

  useEffect(() => {
    const fetchCall = async () => {
      const call = await getCall(id)
      setCall(call)
    }
    fetchCall()
  }, [id])

  useEffect(() => {
    const fetchCallAndAgent = async () => {
      if (callDisconnect) {
        setIsDataReady(true)
        return
      }

      try {
        if (call && call.agentId && call.agentId !== getDefaultAgentId) {
          const [agent, agentUsage] = await Promise.all([
            getAgent(call.agentId),
            getAgentUsage(call.agentId),
          ])
          dispatch({ type: StateAction.setAgent, payload: agent })
          if (!agentUsage.pay_as_you_go_enabled) {
            dispatch({ type: StateAction.setAgentUsage, payload: agentUsage })
          } else {
            dispatch({
              type: StateAction.setAgentUsage,
              payload: {
                pay_as_you_go_enabled: true,
                current_usage: agentUsage.current_usage,
                usage_limit: null,
                subscription_plan: agentUsage.subscription_plan,
              },
            })
          }
          setIsDataReady(true)
        } else if (call && call.agentId === getDefaultAgentId) {
          const agent = await getAgent(getDefaultAgentId)
          dispatch({ type: StateAction.setAgent, payload: agent })
          dispatch({
            type: StateAction.setAgentId,
            payload: getDefaultAgentId,
          })
          setIsDataReady(true)
        }
      } catch (error) {
        console.error("Error fetching call and agent:", error)
        setIsDataReady(true)
      }
    }
    fetchCallAndAgent()
  }, [id, dispatch, call])

  const handleCustomButtonClick = () => {
    window.open(agent?.custom_brand_button_url, "_self")
    setHeadline(
      isDefaultAgent ? "Your demo has ended!" : "Your meeting has ended!",
    )
  }

  const handleGetStartedForFree = () => {
    window.open("https://app.bey.chat/", "_blank")
    logAnalyticsEvent(AnalyticsEvents.AgentVisited)
  }

  const handleEndCallAndSummary = async () => {
    if (!agent?.custom_branding) {
      await endCallDb(id)
      setSummaryOpen(true)
    }
    setHeadline(
      isDefaultAgent ? "Your demo has ended!" : "Your meeting has ended!",
    )
    logAnalyticsEvent(AnalyticsEvents.SummaryRequested)
  }

  const rejoin = async () => {
    logAnalyticsEvent(AnalyticsEvents.Rejoined)
    if (!call) {
      console.error("Cannot rejoin: call is null")
      return
    }
    const isCustomBrandingFlow = customBrandingFlow || agent?.custom_branding
    const agentId = call.agentId
    const path = isCustomBrandingFlow ? `/agent/${agentId}` : `/${agentId}`
    navigate(path)
  }

  const handleClose = () => {
    setFeedbackOpen(false)
    setSummaryOpen(false)
  }

  const emailAddress = useMemo(() => {
    if (agent?.custom_branding) {
      return agent?.custom_brand_email || "support@beyondpresence.ai"
    }
    return "support@beyondpresence.ai"
  }, [agent])

  const topOffset = isEmbedded ? 0 : isMobile ? "5vh" : "10vh"
  const sideOffset = isEmbedded ? 0 : isMobile ? "10px" : "25px"

  return (
    <Box
      sx={{
        position: "relative",
        height: "100vh",
        width: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {!isEmbedded && <Header />}
      {!isDataReady ? (
        <PostScreenLoading />
      ) : (
        <Box
          minHeight={"500px"}
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
          justifyContent={"flex-start"}
          position={"absolute"}
          top={topOffset}
          left={sideOffset}
          right={sideOffset}
          bottom={sideOffset}
          height={isEmbedded ? "100%" : `calc(100% - ${topOffset})`}
          zIndex={1000}
          sx={{
            padding: isEmbedded
              ? "0px"
              : { xs: "5px", sm: "5px 10px 10px 10px" },
            overflow: "auto",
          }}
        >
          {isMobile ? (
            <Box
              display={"flex"}
              flexDirection={"column"}
              height={"100%"}
              width={"100%"}
            >
              <Box flex={1} position={"relative"}>
                <div
                  className="lk-video-conference-inner"
                  style={{
                    height: "100%",
                    width: "100%",
                    position: "relative",
                    minHeight: "400px",
                  }}
                >
                  <Box
                    display={"flex"}
                    flexDirection={"column"}
                    position={"relative"}
                    borderRadius={isEmbedded ? "0px" : "1rem"}
                    overflow={"hidden"}
                    width={"100%"}
                    height={"100%"}
                  >
                    <AgentVideo agent={agent} isEmbed={isEmbedded} />
                    <Box
                      position="absolute"
                      top={0}
                      left={0}
                      width="100%"
                      height="100%"
                      sx={{
                        backdropFilter: "blur(10px)",
                        backgroundColor: "rgba(0, 0, 0, 0.15)",
                      }}
                    />
                    <Stack
                      direction={"column"}
                      spacing={isMobile ? 1.5 : isTablet ? 2.5 : 3.5}
                      sx={{
                        width: "100%",
                        maxWidth: "600px",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 1,
                        padding: "0px 1rem",
                      }}
                    >
                      {!feedbackOpen && (
                        <>
                          <Typography
                            text={
                              callDisconnect?.reason ||
                              (isDefaultAgent
                                ? "Your demo has ended!"
                                : headline)
                            }
                            size={TypographySize.HeadingL}
                            weight={TypographyWeight.SemiBold}
                            color="var(--colors-light-text-text-white)"
                          />
                          {isDefaultAgent && (
                            <Typography
                              text={
                                "Did you enjoy your call? Signup to create your own Agent."
                              }
                              size={TypographySize.TextL}
                              weight={TypographyWeight.Regular}
                              color="var(--colors-light-text-text-white)"
                            />
                          )}
                          {callDisconnect?.description && (
                            <Typography
                              text={callDisconnect.description}
                              size={TypographySize.TextM}
                              weight={TypographyWeight.Regular}
                              color="var(--colors-light-text-text-white)"
                            />
                          )}

                          <Fragment>
                            <Stack
                              direction={"column"}
                              spacing={1.5}
                              sx={{
                                width: "100%",
                                maxWidth: { xs: "100%", sm: "250px" },
                              }}
                            >
                              <MainButton
                                onClick={rejoin}
                                disabled={!call}
                                leadingIcon={"VideoCamera"}
                                showLeadingIcon
                                size={isMobile ? ButtonSize.sm : ButtonSize.lg}
                                text={"Restart Conversation"}
                                hierarchy={
                                  isDefaultAgent
                                    ? ButtonHierarchy.Secondary
                                    : ButtonHierarchy.PrimaryLemon
                                }
                              />
                              {hasCustomButton ? (
                                <MainButton
                                  onClick={handleCustomButtonClick}
                                  size={
                                    isMobile ? ButtonSize.sm : ButtonSize.lg
                                  }
                                  text={agent?.custom_brand_button_text || ""}
                                  hierarchy={ButtonHierarchy.SecondaryColor}
                                />
                              ) : (
                                <MainButton
                                  onClick={
                                    isDefaultAgent
                                      ? handleGetStartedForFree
                                      : handleEndCallAndSummary
                                  }
                                  size={
                                    isMobile ? ButtonSize.sm : ButtonSize.lg
                                  }
                                  text={
                                    isDefaultAgent
                                      ? "Get Started for Free"
                                      : !agent?.custom_branding
                                        ? "End call and send me a summary!"
                                        : "End call!"
                                  }
                                  hierarchy={
                                    isDefaultAgent
                                      ? ButtonHierarchy.PrimaryLemon
                                      : ButtonHierarchy.Primary
                                  }
                                />
                              )}
                            </Stack>
                          </Fragment>
                        </>
                      )}
                      <SnackbarWithText
                        open={summaryOpen}
                        severity={SnackbarType.success}
                        text={`A summary of the meeting was sent to ${email}`}
                        handleClose={handleClose}
                      />
                      <Box sx={{ mt: { xs: 1, sm: 2, md: 3 } }}>
                        {!feedbackSubmitted && !isDefaultAgent && (
                          <Feedback
                            visible={feedbackOpen}
                            onClose={() => setFeedbackOpen(false)}
                            onOpen={() => setFeedbackOpen(true)}
                            onSubmit={() => setFeedbackSubmitted(true)}
                          />
                        )}
                      </Box>
                    </Stack>
                  </Box>
                </div>
              </Box>
            </Box>
          ) : (
            <div
              className="lk-video-conference-inner"
              style={{
                width: getViewWidth(isEmbedded),
                marginRight: "unset",
                margin: "0px auto",
                height: "100%",
                minHeight: "500px",
                display: "flex",
                flexDirection: "column",
                position: "relative",
              }}
            >
              <Box
                display={"flex"}
                flexDirection={"column"}
                position={"relative"}
                borderRadius={isEmbedded ? "0px" : "1rem"}
                overflow={"hidden"}
                width={"100%"}
                height={"100%"}
              >
                <AgentVideo agent={agent} isEmbed={isEmbedded} />
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  width="100%"
                  height="100%"
                  sx={{
                    backdropFilter: "blur(10px)",
                    backgroundColor: "rgba(0, 0, 0, 0.15)",
                  }}
                />
                <Stack
                  direction={"column"}
                  spacing={isMobile ? 1.5 : isTablet ? 2.5 : 3.5}
                  sx={{
                    width: "100%",
                    maxWidth: "600px",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 1,
                  }}
                >
                  {!feedbackOpen && (
                    <>
                      <Box
                        display={"flex"}
                        flexDirection={"column"}
                        gap={
                          "var(--spacing-tokens-size-in-px-spacing-spacing-lg)"
                        }
                      >
                        <Typography
                          text={
                            callDisconnect?.reason ||
                            (isDefaultAgent ? "Your demo has ended!" : headline)
                          }
                          size={TypographySize.HeadingL}
                          weight={TypographyWeight.SemiBold}
                          color="var(--colors-light-text-text-white)"
                        />
                        {isDefaultAgent && (
                          <Typography
                            text={
                              "Did you enjoy your call? Signup to create your own Agent."
                            }
                            size={TypographySize.TextL}
                            weight={TypographyWeight.Regular}
                            color="var(--colors-light-text-text-white)"
                          />
                        )}
                        {callDisconnect?.description && (
                          <Typography
                            text={callDisconnect.description}
                            size={TypographySize.TextM}
                            weight={TypographyWeight.Regular}
                            color="var(--colors-light-text-text-white)"
                          />
                        )}
                      </Box>

                      <Stack
                        direction={"row"}
                        spacing={1.5}
                        justifyContent={"center"}
                        sx={{ width: "100%" }}
                      >
                        <MainButton
                          onClick={rejoin}
                          disabled={!call}
                          leadingIcon={"VideoCamera"}
                          showLeadingIcon
                          size={isMobile ? ButtonSize.sm : ButtonSize.md}
                          text={"Restart Conversation"}
                          hierarchy={
                            isDefaultAgent
                              ? ButtonHierarchy.Secondary
                              : ButtonHierarchy.PrimaryLemon
                          }
                          style={{ flex: 1, maxWidth: "220px" }}
                        />
                        {hasCustomButton ? (
                          <MainButton
                            onClick={handleCustomButtonClick}
                            size={isMobile ? ButtonSize.sm : ButtonSize.md}
                            text={agent?.custom_brand_button_text || ""}
                            hierarchy={ButtonHierarchy.SecondaryColor}
                            style={{ flex: 1, maxWidth: "220px" }}
                          />
                        ) : (
                          <MainButton
                            onClick={
                              isDefaultAgent
                                ? handleGetStartedForFree
                                : handleEndCallAndSummary
                            }
                            size={isMobile ? ButtonSize.sm : ButtonSize.md}
                            text={
                              isDefaultAgent
                                ? "Get Started for Free"
                                : !agent?.custom_branding
                                  ? "End call and send me a summary!"
                                  : "End call!"
                            }
                            hierarchy={
                              isDefaultAgent
                                ? ButtonHierarchy.PrimaryLemon
                                : ButtonHierarchy.Primary
                            }
                            style={{ flex: 1, maxWidth: "220px" }}
                          />
                        )}
                      </Stack>
                    </>
                  )}
                  <SnackbarWithText
                    open={summaryOpen}
                    severity={SnackbarType.success}
                    text={`A summary of the meeting was sent to ${email}`}
                    handleClose={handleClose}
                  />
                  <Box sx={{ mt: { xs: 1, sm: 2, md: 3 } }}>
                    {!feedbackSubmitted && !isDefaultAgent && (
                      <Feedback
                        visible={feedbackOpen}
                        onClose={() => setFeedbackOpen(false)}
                        onOpen={() => setFeedbackOpen(true)}
                        onSubmit={() => setFeedbackSubmitted(true)}
                      />
                    )}
                  </Box>
                </Stack>
              </Box>
            </div>
          )}
        </Box>
      )}
      <Background />
      <Overlay open />
    </Box>
  )
}
